import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Skeleton, TablePagination } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const SoftTable = ({
  columns,
  rows,
  rowKey,
  loading,
  emptyMessage,
  actions,
  pagination,
  minWidth,
  skeletonRows,
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const hasActions = typeof actions === 'function';
  const columnCount = columns.length + (hasActions ? 1 : 0);

  const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, color: theme.palette.text.secondary, fontWeight: 600, whiteSpace: 'nowrap' };
  const td = { padding: '12px', fontSize: 13, color: theme.palette.text.primary, borderTop: `1px solid ${theme.palette.divider}`, verticalAlign: 'middle' };

  const getKey = (row, idx) => (typeof rowKey === 'function' ? rowKey(row) : row[rowKey] ?? idx);

  const openMenu = (e, row) => {
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const menuActions = hasActions && menuRow ? actions(menuRow) : [];

  if (loading) {
    return (
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '14px', p: 2, bgcolor: 'background.paper' }}>
        {[...Array(skeletonRows || 6)].map((_, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.2 }}>
            {columns.map((c) => (
              <Skeleton key={c.key} variant="text" width={c.skeletonWidth || '16%'} animation="wave" />
            ))}
            {hasActions && <Skeleton variant="circular" width={24} height={24} animation="wave" sx={{ ml: 'auto' }} />}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '14px', overflow: 'hidden', bgcolor: 'background.paper' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: minWidth || 720 }}>
            <thead>
              <tr style={{ backgroundColor: theme.palette.action.hover }}>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    style={{ ...th, ...(c.width ? { width: c.width } : {}), ...(c.align ? { textAlign: c.align } : {}) }}
                  >
                    {c.label}
                  </th>
                ))}
                {hasActions && <th style={{ ...th, width: 56, textAlign: 'right' }} />}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} style={{ ...td, borderTop: 'none', textAlign: 'center', padding: '32px' }}>
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={getKey(row, idx)}>
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        style={{ ...td, ...(c.nowrap ? { whiteSpace: 'nowrap' } : {}), ...(c.align ? { textAlign: c.align } : {}) }}
                      >
                        {c.render ? c.render(row) : row[c.key]}
                      </td>
                    ))}
                    {hasActions && (
                      <td style={{ ...td, textAlign: 'right' }}>
                        {actions(row).length > 0 && (
                          <IconButton size="small" onClick={(e) => openMenu(e, row)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
      </Box>

      {pagination && pagination.count > 0 && (
        <TablePagination
          component="div"
          count={pagination.count}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          rowsPerPageOptions={pagination.rowsPerPageOptions || [10, 25, 50, 100]}
          onPageChange={pagination.onPageChange}
          onRowsPerPageChange={pagination.onRowsPerPageChange}
          labelRowsPerPage={pagination.labelRowsPerPage}
          labelDisplayedRows={pagination.labelDisplayedRows || (({ from, to, count }) => `${from}-${to} / ${count}`)}
        />
      )}

      {hasActions && (
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
          {menuActions.map((a) => (
            <MenuItem
              key={a.id}
              onClick={() => {
                closeMenu();
                a.onClick();
              }}
              sx={{ fontSize: 13, gap: 1 }}
            >
              {a.icon} {a.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
};

export default SoftTable;
